import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarCheck, Download, Loader2, MapPin, RotateCcw, Search, Truck, ExternalLink } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { STATUS_LABEL, ALL_SHIPPED_STATUS_IDS } from '@/features/orders/constants';
import { toCommaList } from '@/utils/search-list';

type Day = 'yesterday' | 'today' | 'tomorrow' | 'all';
interface Row {
  id: number; lrNo: string;
  merchant?: { id: number; companyName: string } | null;
  appointmentDate?: string | null; appointmentTime?: string | null; appointmentRef?: string | null;
  poNumber?: string | null; poExpiryDate?: string | null; poExpired?: boolean;
  courierName?: string | null; boxes?: number;
  consigneeName?: string | null; consigneeCity?: string | null; consigneePincode?: string | null;
  statusLabel?: string | null; notDelivered?: boolean; edd?: string | null;
  order?: { id?: string; orderType?: string; orderNo?: string } | null;
}
interface ListResp { total: number; items: Row[]; counts: Record<'yesterday' | 'today' | 'tomorrow' | 'all', number> }
interface Options { couriers: { id: number; name: string }[]; merchants: { id: number; companyName: string }[]; slots: string[] }

const TABS: { id: Day; label: string }[] = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'all', label: 'All' },
];
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—');

/**
 * Gaint Appointments — every delivery slot already booked for a consignment on Gaint's own couriers,
 * by day: Yesterday / Today / Tomorrow / All. The branch plans the day's runs from "Today" and
 * "Tomorrow"; "Yesterday" is the check that every slot was met.
 *
 * Read-only on purpose: slots are booked and changed in Manage Appointments. Gated by the
 * 'Gaint Appointments' permission (view = this screen, export = the CSV).
 */
export function GaintAppointmentsPage() {
  const [day, setDay] = useState<Day>('today');
  const [lrNo, setLrNo] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [consignee, setConsignee] = useState('');
  const [city, setCity] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [courierIds, setCourierIds] = useState<(string | number)[]>([]);
  const [statusIds, setStatusIds] = useState<(string | number)[]>([]);
  const [slot, setSlot] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [exporting, setExporting] = useState(false);

  const { data: options } = useQuery<Options>({
    queryKey: ['gaint-appointments-filters'],
    queryFn: async () => (await api.get<Options>('/appointments/gaint/filters')).data,
    staleTime: 5 * 60_000,
  });

  const filters = useMemo(() => {
    const q: Record<string, string> = {};
    if (lrNo.trim()) q.lrNo = lrNo.trim();
    if (poNumber.trim()) q.poNumber = poNumber.trim();
    if (consignee.trim()) q.consignee = consignee.trim();
    if (city.trim()) q.city = city.trim();
    if (merchantId) q.merchantId = merchantId;
    if (courierIds.length) q.courierIds = courierIds.join(',');
    if (statusIds.length) q.statusIds = statusIds.join(',');
    if (slot) q.slot = slot;
    if (from) q.from = from;
    if (to) q.to = to;
    return q;
  }, [lrNo, poNumber, consignee, city, merchantId, courierIds, statusIds, slot, from, to]);

  const { data, isLoading, error } = useQuery<ListResp>({
    queryKey: ['gaint-appointments', day, filters, page, pageSize],
    queryFn: async () => (await api.get<ListResp>('/appointments/gaint', { params: { ...filters, day, page, pageSize } })).data,
  });

  const reset = () => {
    setLrNo(''); setPoNumber(''); setConsignee(''); setCity(''); setMerchantId('');
    setCourierIds([]); setStatusIds([]); setSlot(''); setFrom(''); setTo(''); setPage(1);
  };
  const onFilter = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ ...filters, day }).toString();
      await downloadBlob(`/appointments/gaint/export?${qs}`, `gaint-appointments-${day}-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const denied = (error as any)?.response?.status === 403;

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarCheck size={22} /> Gaint Appointments</h1>
          <p className="text-sm text-slate-500">Delivery slots already booked for consignments on Gaint's own couriers</p>
        </div>
        <button onClick={exportCsv} disabled={exporting || total === 0}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export
        </button>
      </div>

      {/* Day tabs — counted under the same filters as the list. */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
        {TABS.map((t) => {
          const n = data?.counts?.[t.id];
          const on = day === t.id;
          return (
            <button key={t.id} onClick={() => { setDay(t.id); setPage(1); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition flex items-center gap-2 ${on ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {t.label}
              {n != null && <span className={`px-1.5 py-0.5 rounded-full text-[11px] ${on ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-3">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2 items-end">
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">LR number</label>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={lrNo} onChange={(e) => onFilter(setLrNo)(toCommaList(e.target.value))} placeholder="One or many" className="input w-full font-mono pl-7" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">PO number</label>
            <input value={poNumber} onChange={(e) => onFilter(setPoNumber)(e.target.value)} placeholder="PO" className="input w-full font-mono" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Consignee</label>
            <input value={consignee} onChange={(e) => onFilter(setConsignee)(e.target.value)} placeholder="Name" className="input w-full" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">City</label>
            <input value={city} onChange={(e) => onFilter(setCity)(e.target.value)} placeholder="City" className="input w-full" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Merchant</label>
            <select value={merchantId} onChange={(e) => onFilter(setMerchantId)(e.target.value)} className="input w-full">
              <option value="">All merchants</option>
              {(options?.merchants ?? []).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Courier</label>
            <MultiSelectFilter label="" placeholder="All Gaint couriers" values={courierIds} onChange={onFilter(setCourierIds)}
              options={(options?.couriers ?? []).map((c) => ({ value: c.id, label: c.name }))} />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Slot</label>
            <select value={slot} onChange={(e) => onFilter(setSlot)(e.target.value)} className="input w-full">
              <option value="">Any slot</option>
              {(options?.slots ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Shipment status</label>
            <MultiSelectFilter label="" placeholder="Any status" values={statusIds} onChange={onFilter(setStatusIds)}
              options={ALL_SHIPPED_STATUS_IDS.map((id) => ({ value: id, label: STATUS_LABEL[id] ?? `Status ${id}`, hint: '#' + id }))} />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Appointment from</label>
            <input type="date" value={from} onChange={(e) => onFilter(setFrom)(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Appointment to</label>
            <input type="date" value={to} onChange={(e) => onFilter(setTo)(e.target.value)} className="input w-full" />
          </div>
          <div className="flex items-end">
            <button onClick={reset} className="px-3 py-2 border border-slate-200 rounded-lg text-xs flex items-center gap-1.5 hover:bg-slate-50"><RotateCcw size={12} /> Reset</button>
          </div>
          <div className="flex items-end justify-end text-xs text-slate-500 pb-2">{total.toLocaleString('en-IN')} appointment{total === 1 ? '' : 's'}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">LR details</th>
                <th className="text-left px-3 py-2.5">Courier</th>
                <th className="text-left px-3 py-2.5">Consignee</th>
                <th className="text-left px-3 py-2.5">Appointment</th>
                <th className="text-left px-3 py-2.5">PO</th>
                <th className="text-left px-3 py-2.5">Shipment status</th>
                <th className="text-left px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="text-center py-10 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>}
              {!isLoading && denied && (
                <tr><td colSpan={7} className="text-center py-12 text-rose-600">You don't have permission to view Gaint Appointments. Ask an administrator to grant it under Users → Roles &amp; Permissions.</td></tr>
              )}
              {!isLoading && !denied && items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">
                  {day === 'all' ? 'No booked Gaint appointments for these filters.' : `No Gaint appointments ${day === 'today' ? 'today' : day}.`}
                </td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60 align-top">
                  <td className="px-3 py-2.5">
                    <div className="font-mono font-medium text-slate-900">{r.lrNo}</div>
                    <div className="text-[11px] text-slate-500">{r.boxes ?? 1} box{(r.boxes ?? 1) === 1 ? '' : 'es'}{r.merchant?.companyName ? ` · ${r.merchant.companyName}` : ''}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-800"><span className="flex items-center gap-1"><Truck size={12} className="text-slate-400" />{r.courierName ?? '—'}</span></td>
                  <td className="px-3 py-2.5">
                    <div className="text-slate-800">{r.consigneeName || '—'}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1"><MapPin size={10} />{[r.consigneeCity, r.consigneePincode].filter(Boolean).join(', ') || '—'}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <div className="text-slate-900 font-medium">{fmtDate(r.appointmentDate)}</div>
                    <div className="text-slate-600">{r.appointmentTime || '—'}</div>
                    {r.appointmentRef && <div className="text-[10px] text-slate-400 font-mono truncate max-w-[160px]" title={r.appointmentRef}>No. {r.appointmentRef}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <div className="font-mono text-slate-800 truncate max-w-[160px]" title={r.poNumber ?? ''}>{r.poNumber || '—'}</div>
                    <div className={r.poExpired ? 'text-rose-600 font-medium' : 'text-slate-500'}>
                      {r.poExpiryDate ? `till ${fmtDate(r.poExpiryDate)}${r.poExpired ? ' · expired' : ''}` : 'no expiry'}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {r.statusLabel
                      ? <span className={`px-1.5 py-0.5 rounded text-[11px] ${r.notDelivered ? 'bg-amber-50 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`}>{r.statusLabel}</span>
                      : <span className="text-slate-400">—</span>}
                    {r.edd && <div className="text-[11px] text-slate-500 mt-0.5">Delivery by {fmtDate(r.edd)}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                    {r.order?.id && (
                      <Link to={`/orders-${String(r.order.orderType ?? 'B2B').toLowerCase()}/${r.order.id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                        Order <ExternalLink size={11} />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-xs text-slate-600">
          <div>Showing {total === 0 ? 0 : (page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total.toLocaleString('en-IN')}</div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40">Previous</button>
            <span>Page {page} of {lastPage}</span>
            <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40">Next</button>
          </div>
          <div className="flex items-center gap-2">
            Rows per page
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="input py-1 text-xs">
              {[20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
